/**
 * Alur multi-mineral sebagai alur — empat tahap, berdiri sendiri.
 *
 * Sampai kini penyelesai multi-mineral tidak punya alur sama sekali. Ia
 * menumpang di layar alur deterministik: rel kirinya menyebut enam tahap
 * deterministik, dan seluruh sisa layar — kolom tengah maupun rel QC — adalah
 * multi-mineral. Dua tafsiran yang menjawab pertanyaan berbeda dari log yang
 * sama karena itu dibaca sebagai satu tafsiran dengan dua bagian, dan tidak ada
 * satu tempat pun yang mengatakan apa yang sudah dan belum dikerjakan
 * penyelesainya.
 *
 * Keempat tahap di sini bukan pembagian yang dikarang untuk menyamai yang
 * enam. Ia urutan yang memang dikerjakan orang, dan tiap tahap menjawab satu
 * pertanyaan yang tidak bisa dijawab tahap lain:
 *
 *   1. mineral apa yang dicari di batuan ini,
 *   2. alat mana yang boleh ikut memutuskan, dan seberapa dipercaya,
 *   3. apa yang boleh dan tidak boleh dilakukan penyelesainya,
 *   4. dan apa hasilnya ketika dijalankan.
 *
 * Lampunya menyala dari fakta, bukan dari "tahap ini sudah dibuka". Sebuah
 * lampu hijau yang berarti "sudah dilihat orang" tidak mengatakan apa pun
 * tentang hasilnya.
 */
import type { AnalysisRun, MineralModel } from "../types";
import { guessRole } from "./MineralTool";
import { MethodButton, StatusDot } from "./ui";

/** Satu tahap sebagaimana relnya perlu mengetahuinya. */
export interface MultiminStage {
  n: number;
  name: string;
  /** Ringkasan sebaris — apa yang berlaku sekarang, bukan apa yang bisa dipilih. */
  method: string;
  /** Baris kedua: angka yang menjelaskan ringkasan di atasnya. */
  params: string;
  ok: boolean;
  /** Judul tombolnya bila tahap ini punya jendela; kosong berarti kotak diam. */
  opens?: string;
}

const count = (n: number, singular: string, plural = `${singular}s`) =>
  `${n} ${n === 1 ? singular : plural}`;

/**
 * Incoherence tertinggi yang masih boleh menyalakan lampu hijau, dalam sigma.
 *
 * Incoherence adalah akar rata-rata kuadrat sisa **dalam satuan sigma alatnya
 * masing-masing**, jadi 1,0 berarti tiap alat meleset rata-rata satu simpangan
 * baku — begitu `petro/multimin.py` mendefinisikannya. Dua adalah garis "dalam
 * dua simpangan baku" yang lazim, dan ia dituliskan di sini alih-alih
 * disembunyikan di dalam sebuah perbandingan, sebab ia satu-satunya angka di
 * berkas ini yang merupakan penilaian dan bukan bacaan.
 *
 * Tanpa ambang ini lampunya hanya membaca `converged`, dan konvergen bukan
 * cocok: TSM-005 di Prabumulih memulangkan `converged: true` dengan 0 dari
 * 1.667 titik gagal — dan incoherence 11,77 σ. Setiap titik mencapai
 * toleransinya, dan jawabannya tetap sebelas simpangan baku dari lognya. Lampu
 * hijau di atasnya menjawab pertanyaan yang lain daripada yang tertulis di
 * sebelahnya.
 */
export const INCOHERENCE_LIMIT = 2.0;

/**
 * Keempat tahap, dibaca dari model yang hidup dan solve terakhirnya.
 *
 * Fungsi murni supaya ia bisa diuji tanpa menyalakan React, dan supaya yang
 * memutuskan warna sebuah lampu berdiri di satu tempat yang bisa dibaca.
 */
export function multiminStages(
  model: MineralModel | null,
  run: AnalysisRun | null,
): MultiminStage[] {
  const components = model?.components ?? [];
  const tools = model?.tools ?? [];
  const on = tools.filter((tool) => tool.enabled);
  const constraints = (model?.constraints ?? {}) as Record<string, unknown>;
  const solver = run?.qc?.solver ?? null;

  // Lewat `guessRole` dan bukan lewat `component.role` mentah: model bawaan
  // menyimpan `role: null` pada keenam komponennya dan menyerahkan penebakan
  // kepada server, jadi pembacaan mentah menghitung nol fluida di atas model
  // yang porinya lengkap — dan menyalakan lampu kuning pada tahap yang tidak
  // punya cacat apa pun.
  const fluids = components.filter((component) =>
    ["air_terikat", "air_bebas", "hidrokarbon"].includes(guessRole(component)),
  ).length;

  // Derajat kebebasan sistemnya: sebuah campuran n komponen punya n−1 volume
  // bebas sesudah syarat jumlah satu, dan tiap alat yang menyala memberi satu
  // persamaan. Kurang dari itu bukan galat — penyelesainya meredam dan tetap
  // menjawab — tetapi jawabannya sebagian datang dari redaman dan bukan dari
  // log, dan itu yang perlu terbaca sebelum solve-nya dipercaya.
  const bebas = Math.max(components.length - 1, 0);
  const determined = on.length >= bebas;
  const fits =
    solver?.mean_incoherence != null && solver.mean_incoherence <= INCOHERENCE_LIMIT;

  return [
    {
      n: 1,
      name: "Mineral components",
      method: model ? `${count(components.length, "component")} · v${model.version}` : "—",
      params: model
        ? `${count(fluids, "fluid")} · ${count(components.length - fluids, "solid")}`
        : "no model yet",
      // Dua komponen adalah jumlah terkecil yang masih bisa dipisahkan satu
      // sama lain, dan satu di antaranya harus menempati pori — tanpa itu
      // porositasnya nol di mana-mana menurut modelnya sendiri.
      ok: components.length >= 2 && fluids >= 1,
      opens: "Edit the mineral components",
    },
    {
      n: 2,
      name: "Tool responses",
      method: on.length ? on.map((tool) => tool.name).join(" · ") : "none switched on",
      params: determined
        ? `${on.length} of ${tools.length} on · ${bebas} unknowns`
        : `${on.length} equations for ${bebas} unknowns — underdetermined`,
      ok: on.length > 0 && determined,
    },
    {
      n: 3,
      name: "Constraints",
      method: [
        constraints.unity ? "unity" : "",
        constraints.non_negative ? "non-negative" : "",
      ]
        .filter(Boolean)
        .join(" · ") || "unconstrained",
      params: [
        constraints.max_clay != null ? `max clay ${Number(constraints.max_clay).toFixed(2)}` : "",
        constraints.tolerance != null ? `tol ${Number(constraints.tolerance)}` : "",
        constraints.max_iterations != null ? `${constraints.max_iterations} iter` : "",
      ]
        .filter(Boolean)
        .join(" · "),
      // Tanpa syarat jumlah satu dan tanpa larangan volume negatif, penyelesai
      // kuadrat terkecil boleh menjawab −0,3 kuarsa dan 1,3 lempung: sebuah
      // penyelesaian yang benar secara aljabar dan bukan batuan mana pun.
      ok: Boolean(constraints.unity) && Boolean(constraints.non_negative),
    },
    {
      n: 4,
      name: "Solve",
      method: solver
        ? solver.nonlinear
          ? "Levenberg–Marquardt"
          : "Least squares"
        : "not solved yet",
      params: solver
        ? [
            solver.mean_incoherence == null
              ? "incoherence —"
              : `incoherence ${solver.mean_incoherence.toFixed(2)} σ`,
            `${solver.failed_points} of ${solver.samples} failed`,
            solver.regularised ? `damped ${solver.damping}` : "",
            // Konvergen dan tetap jauh dari lognya: keadaan yang paling mudah
            // disalahbaca, sebab tiap angka lain di baris ini terlihat sehat.
            solver.converged && !fits ? `fit worse than ${INCOHERENCE_LIMIT} σ` : "",
          ]
            .filter(Boolean)
            .join(" · ")
        : "run the solve to see its incoherence",
      // Dua syarat, bukan satu. Konvergensi mengatakan penyelesainya selesai
      // bekerja; incoherence mengatakan apakah yang diselesaikannya menjelaskan
      // lognya. Sebuah solve yang gagal di separuh titiknya tetap memulangkan
      // kurva, dan sebuah solve yang konvergen pada 11,77 σ memulangkan kurva
      // yang tidak menjelaskan satu alat pun.
      ok: Boolean(solver?.converged) && fits,
    },
  ];
}

/**
 * Kartu tahapnya, sebangun dengan kartu alur deterministik di sebelahnya.
 *
 * Sengaja sebangun: keduanya alur atas sumur yang sama, dan dua rel yang
 * digambar dengan dua cara berbeda membuat pembacanya mengira keduanya dua
 * jenis benda yang berbeda.
 */
export function MultiminStages({
  stages,
  mayCompute,
  onOpen,
}: {
  stages: MultiminStage[];
  mayCompute: boolean;
  /** Dipanggil dengan nomor tahap yang jendelanya diminta. */
  onOpen: (stage: number) => void;
}) {
  return (
    <>
      {stages.map((stage) => (
        <div
          key={stage.n}
          style={{
            display: "flex",
            gap: 10,
            padding: "10px 11px",
            background: "var(--panel)",
            border: "1px solid var(--border-control)",
            borderRadius: "var(--radius-panel)",
            boxShadow: "var(--shadow-panel)",
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 19,
              height: 19,
              flex: "none",
              borderRadius: "50%",
              background: "var(--control)",
              border: "1px solid var(--border-hover)",
              font: "600 9.5px var(--font-mono)",
              color: "var(--ink-2)",
            }}
          >
            {stage.n}
          </span>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 5 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span style={{ font: "600 11px var(--font-sans)", color: "var(--ink)" }}>
                {stage.name}
              </span>
              <span style={{ flex: 1 }} />
              <StatusDot tone={stage.ok ? "ok" : "warn"} />
            </div>
            {stage.opens ? (
              <MethodButton
                title={stage.opens}
                disabled={!mayCompute}
                onClick={() => onOpen(stage.n)}
              >
                {stage.method}
              </MethodButton>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: 25,
                  padding: "0 8px",
                  background: "var(--control)",
                  border: "1px solid var(--border-control)",
                  borderRadius: "var(--radius)",
                  font: "500 10.5px var(--font-sans)",
                  color: "var(--ink)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {stage.method}
              </div>
            )}
            <span style={{ font: "400 9.5px/1.4 var(--font-mono)", color: "var(--ink-6)" }}>
              {stage.params}
            </span>
          </div>
        </div>
      ))}
    </>
  );
}
